import { ModuleMultiSelect } from '../module-multiselect';
import '../../assets/course-editor.css';
import { useContext, useState, useMemo, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { MultiSelect } from "primereact/multiselect";
import { Panel } from "primereact/panel";

import { CourseManagementDataContext } from "../../providers/course-management-provider";

export function CourseEditorGeneralTab() {
    const ctx = useContext(CourseManagementDataContext);

    // -------------------------------
    // Hooks MUST come before any return
    // -------------------------------

    const course = ctx?.selectedCourseFull ?? null;

    // Module filtering state
    const [moduleSearch, setModuleSearch] = useState("");
    const [debouncedModuleSearch, setDebouncedModuleSearch] = useState("");

    // Debounce module search
    useEffect(() => {
        const handle = setTimeout(() => {
            setDebouncedModuleSearch(moduleSearch.trim().toLowerCase());
        }, 150);
        return () => clearTimeout(handle);
    }, [moduleSearch]);

    // Filtered module options
    const moduleOptions = useMemo(() => {
        if (!ctx) return [];

        return ctx.allModules
            .filter(m => {
                if (!debouncedModuleSearch) return true;
                const s = debouncedModuleSearch;
                return (
                    m.name.toLowerCase().includes(s) ||
                    m.module_key.toLowerCase().includes(s)
                );
            })
            .map(m => ({
                label: m.name,
                value: m.module_key,
                managed: m.is_managed
            }));
    }, [ctx, debouncedModuleSearch]);

    // Prerequisite options (unchanged)
    const prerequisiteOptions = useMemo(() => {
        if (!ctx || !course) return [];
        return ctx.allCourses
            .filter(c => c.course_key !== course.course_key)
            .map(c => ({
                label: c.name,
                value: c.course_key
            }));
    }, [ctx, course]);

    // -------------------------------
    // Conditional returns AFTER hooks
    // -------------------------------
    if (!ctx) return null;
    if (!course) return <div className="text-gray-500 p-3">No course selected.</div>;

    // -------------------------------
    // Render
    // -------------------------------
    return (
        <div className="flex flex-column gap-4">

            {/* Basic Info */}
            <Panel header="Basic information">
                <div className="flex flex-column gap-3">

                    {/* Course Name */}
                    <div className="flex flex-column gap-1">
                        <label htmlFor="course-name" className="font-semibold">Course name</label>
                        <InputText
                            id="course-name"
                            value={course.name}
                            onChange={(e) => ctx.updateCourseField("name", e.target.value)}
                            className="w-full"
                        />
                    </div>

                    {/* Description */}
                    <div className="flex flex-column gap-1">
                        <label htmlFor="course-price" className="font-semibold">Price (£)</label>
                        <InputNumber inputId="course-price" value={course.price ?? null}
                            onValueChange={e => ctx.updateCourseField('price', e.value ?? null)}
                            mode="currency" currency="GBP" locale="en-GB"
                            min={0.01} max={9999999.99} minFractionDigits={2} maxFractionDigits={2}
                            required placeholder="Enter price" className="w-full" />
                        <small>Required. Price must be greater than £0.00 and includes applicable taxes.</small>
                    </div>

                    <div className="flex flex-column gap-1">
                        <label htmlFor="course-description" className="font-semibold">Description</label>
                        <small>Markdown supported: **bold**, *italic*, lists and links.</small>
                        <InputTextarea
                            id="course-description"
                            value={course.description ?? ""}
                            onChange={(e) => ctx.updateCourseField("description", e.target.value)}
                            autoResize
                            rows={4}
                            className="w-full"
                        />
                    </div>

                </div>
            </Panel>

            <Panel header="Certificate">
                <div className="flex flex-column gap-3">
                    <label className="flex align-items-center gap-2">
                        <input type="checkbox" checked={course.certificate_enabled !== false}
                            onChange={e => ctx.updateCourseField('certificate_enabled', e.target.checked)} />
                        Issue certificate on completion
                    </label>
                    <small>{course.certificate_enabled === false
                        ? 'No new certificate will be generated and learners will not be asked to confirm a certificate name. Previously issued certificates are retained.'
                        : 'A certificate is issued after the learner completes all course modules.'}</small>
                    <div className="flex flex-column gap-1">
                        <label htmlFor="course-cert-description" className="font-semibold">Certificate description</label>
                        <InputTextarea id="course-cert-description" value={course.cert_description ?? ''}
                            disabled={course.certificate_enabled === false}
                            onChange={e => ctx.updateCourseField('cert_description', e.target.value)}
                            rows={4} autoResize maxLength={4000} className="w-full"
                            aria-describedby="course-cert-description-help" />
                        <small id="course-cert-description-help">Text printed on the certificate, separate from the website description.</small>
                    </div>
                    <div className="flex flex-column gap-1">
                        <label htmlFor="course-cert-prefix" className="font-semibold">Certificate ID prefix</label>
                        <InputText id="course-cert-prefix" value={course.cert_prefix ?? ''}
                            disabled={course.certificate_enabled === false}
                            onChange={e => ctx.updateCourseField('cert_prefix', e.target.value)}
                            maxLength={50} placeholder="For example, TRIAD-CORE-" className="w-full"
                            aria-describedby="course-cert-prefix-help" />
                        <small id="course-cert-prefix-help">Printed before the numeric certificate number. Include any separator you want to use.</small>
                    </div>
                </div>
            </Panel>

            {/* Course Prerequisites */}
            <Panel header="Course prerequisites">
                <MultiSelect
                    value={course.course_prerequisites.map(p => p.course_key)}
                    options={prerequisiteOptions}
                    onChange={(e) => ctx.updateCoursePrerequisites(e.value)}
                    placeholder="Select prerequisite courses"
                    className="w-full"
                    display="chip"
                />
            </Panel>

            {/* Modules Included */}
            <Panel header="Modules included">
                <div className="flex flex-column gap-3">

                    {/* ⭐ Search bar for modules */}
                    <span className="p-input-icon-left w-full">
                        <i className="pi pi-search" />
                        <InputText
                            value={moduleSearch}
                            onChange={(e) => setModuleSearch(e.target.value)}
                            placeholder="Search modules..."
                            className="w-full pl-5"
                        />
                    </span>

                    <ModuleMultiSelect
                        inputId="course-modules" aria-label="Modules included"
                        value={course.modules.map(m => m.module_key)}
                        options={moduleOptions}
                        selectedOptions={ctx.allModules.map(m => ({ label: m.name, value: m.module_key, managed: m.is_managed }))}
                        onChange={ctx.updateCourseModules}
                        placeholder="Select modules for this course"
                        className="w-full"
                        display="chip"
                        filter={false} // we handle filtering ourselves
                    />

                    <div className="text-gray-500 text-sm mt-2">
                        Module ordering and dependencies are edited in the “Modules & dependencies” tab.
                    </div>
                </div>
            </Panel>

        </div>
    );
}
